import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

// Types
import { AppDispatch } from 'types/RootState.types';
import { DeliveryChoiceTabProps, DeliveryChoiceTabsProps } from './DeliveryChoiceTabs.types';

// Config
import deliveryChoiceTabsState from './DeliveryChoiceTabs.state';
import { setSelectedDeliveryChoiceId } from '@redux/actions/delivery/deliveryActions';
import {
  getTabTitleWithCostInfo,
  getIcon,
} from '../deliveryChoiceUtils';

// Components
import Heading from '../../heading';

// Styles
import styles from './delivery-choice-tabs.scss';

const DeliveryChoiceTabs = ({
  tabs = [],
}: DeliveryChoiceTabsProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    basketTotalValue,
    selectedDeliveryChoiceId,
    isDeliveryThresholdIncreaseEnabled,
  } = useSelector(deliveryChoiceTabsState);

  if (tabs?.length !== 2) {
    return null;
  }

  const renderTabContent = (tab: DeliveryChoiceTabProps, tabsLength: number) => {
    const tabProps: DeliveryChoiceTabProps = {
      'aria-hidden': true,
      label: tab.label || '',
      tag: 'h3',
      type: 'xs',
    };

    if (tab?.deliveryId && tabsLength === 2) {
      const tabTextWithCostInfo = getTabTitleWithCostInfo(
        basketTotalValue,
        tab?.deliveryId,
        false,
        isDeliveryThresholdIncreaseEnabled,
      );

      if (tabTextWithCostInfo) {
        return (
          <>
            {tabTextWithCostInfo.toLowerCase().includes('free') ?
              <Heading {...tabProps} className={styles.heading} label={tabTextWithCostInfo} />
              :
              <>
                <Heading {...tabProps} className={styles.heading} />
                {tabTextWithCostInfo &&
                  <p className={styles.subText}>{tabTextWithCostInfo}</p>}
              </>}
          </>
        );
      }

      return <Heading {...tabProps} className={styles.heading} />;
    }

    return <Heading {...tabProps} />;
  };

  return (
    <>
      <ul className={styles.tabList} role="tablist">
        {tabs.map((tab) => {
          const isSelected = tab.isAvailable && (tab.deliveryId === selectedDeliveryChoiceId);
          const selectedString = isSelected ? 'selected' : 'un-selected';

          const showCollectionTab = !tab.collectionPointServiceDown && tab.isAvailable;
          const Icon = getIcon(tab.deliveryId, isSelected);

          if (showCollectionTab) {
            return (
              <li
                key={tab.deliveryId}
                className={styles.tab}
                data-testid={`${tab.deliveryId}-tab`}
                role="presentation"
              >
                <button
                  aria-selected={isSelected}
                  className={cx(styles.button, {
                    [styles.selected]: isSelected,
                  })}
                  data-testid={`${tab.deliveryId}-button`}
                  disabled={tab.disabled}
                  id={tab.deliveryId}
                  onClick={() => dispatch(setSelectedDeliveryChoiceId(tab.deliveryId))}
                  role="tab"
                  type="button"
                >
                  <Icon className={styles.icon} size="large" />
                  <div>
                    {renderTabContent(tab, tabs.length)}
                    <span className="visually-hidden">{tab.label} {selectedString}</span>
                  </div>
                </button>
              </li>
            );
          }

          return null;
        })}
      </ul>
    </>
  );
};

export default DeliveryChoiceTabs;
